import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface AdminSearchPickerFetchResult<T> {
  items: T[];
  lastKey?: string;
}

export interface AdminSearchPickerProps<T> {
  id: string;
  placeholder: string;
  disabled?: boolean;
  debounceMs?: number;
  emptyHint?: string;
  disabledIds?: string[];
  fetchPage: (query: string, lastKey?: string) => Promise<AdminSearchPickerFetchResult<T>>;
  getOptionId: (item: T) => string;
  formatOptionLabel: (item: T) => string;
  onSelect: (item: T) => void;
  committedLabel?: string;
  onClear?: () => void;
  clearAriaLabel?: string;
}

function disabledSet(ids?: string[]): Set<string> {
  return new Set(ids || []);
}

export default function AdminSearchPicker<T>(props: AdminSearchPickerProps<T>) {
  const {
    id,
    placeholder,
    disabled,
    debounceMs = 400,
    emptyHint = 'Type at least 2 non-space characters to search.',
    disabledIds,
    fetchPage,
    getOptionId,
    formatOptionLabel,
    onSelect,
    committedLabel = '',
    onClear,
    clearAriaLabel = 'Clear selection',
  } = props;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [listLastKey, setListLastKey] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const skipNextFetch = useRef(false);

  const runFetch = useCallback(
    async (q: string, append: boolean, pageKey?: string) => {
      if (disabled) return;
      const dis = disabledSet(disabledIds);
      const t = q.trim();
      if (t.length < 2 && !append) {
        setItems([]);
        setListLastKey(undefined);
        setLoading(false);
        return;
      }
      if (t.length < 2 && append) {
        return;
      }
      setLoading(true);
      try {
        const res = await fetchPage(q.trim(), pageKey);
        const next = (res.items || []).filter((it) => !dis.has(getOptionId(it)));
        if (append) {
          setItems((prev) => [...prev, ...next]);
        } else {
          setItems(next);
        }
        setListLastKey(res.lastKey || undefined);
        setHighlight(0);
      } catch {
        if (!append) setItems([]);
        setListLastKey(undefined);
      } finally {
        setLoading(false);
      }
    },
    [disabled, disabledIds, fetchPage, getOptionId]
  );

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runFetch(query, false);
    }, debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, debounceMs, runFetch]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const displayValue = open ? query : committedLabel;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && items.length > 0) {
      e.preventDefault();
      const it = items[highlight];
      if (it) {
        skipNextFetch.current = true;
        onSelect(it);
        setOpen(false);
        setQuery('');
        setItems([]);
        setListLastKey(undefined);
      }
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          id={id}
          type="text"
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          style={{
            flex: 1,
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
          }}
        />
        {onClear && committedLabel && (
          <button type="button" className="btn-sm btn-secondary" onClick={onClear} aria-label={clearAriaLabel}>
            Clear
          </button>
        )}
      </div>
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 50,
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 240,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {loading && items.length === 0 && <div style={{ padding: '0.75rem', color: '#666' }}>Loading…</div>}
          {!loading && items.length === 0 && query.trim().length < 2 && (
            <div style={{ padding: '0.75rem', color: '#666' }}>{emptyHint}</div>
          )}
          {!loading && items.length === 0 && query.trim().length >= 2 && (
            <div style={{ padding: '0.75rem', color: '#666' }}>No matches.</div>
          )}
          {items.map((it, idx) => (
            <div
              key={getOptionId(it)}
              role="option"
              aria-selected={idx === highlight}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                skipNextFetch.current = true;
                onSelect(it);
                setOpen(false);
                setQuery('');
                setItems([]);
                setListLastKey(undefined);
              }}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                background: idx === highlight ? '#e3f2fd' : undefined,
              }}
            >
              {formatOptionLabel(it)}
            </div>
          ))}
          {listLastKey && items.length > 0 && (
            <div style={{ borderTop: '1px solid #eee' }}>
              <button
                type="button"
                className="btn-sm"
                style={{ width: '100%', borderRadius: 0 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void runFetch(query, true, listLastKey)}
              >
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
